import Model from "@/lib/model/model";
import { EnumQuestionType } from "@/lib/model/exambuilder/question-v2";
import request from "@/lib/request/request";
import {
  ExamComponent,
  SectionConfig,
  SectionPageType
} from "@/lib/model/exambuilder/exam-component/exam";

export enum EnumQuestionAppendAskType {
  EnumQuestionAppendAskTypeConst = 1, // 固定追问
  EnumQuestionAppendAskTypeAIGC = 2, // AIGC追问
  EnumQuestionAppendAskTypeSwitch = 3 // 条件追问
}

export enum EnumQuestionAppendConditionType {
  Const = 1, // 100%追问
  Count // 达到字数追问
}

export enum EnumQuestionAppendAIGCAskType {
  GPT = "GPT",
  INFO = "INFO",
  STAR = "STAR",
  FOLLOWUP_BOT_COMMON = "FollowupBotCommon" // 通用
}

export class ReplyCheckpointList extends Model {
  checkpointName = ""; // 触发条件
  replyTextList: string[] = [""]; // 追问话术列表
  loading = false;

  get replyText() {
    return this.replyTextList.join("\n");
  }
  set replyText(val: string) {
    this.replyTextList = val.split("\n");
  }

  public objectArrayClasses() {
    return {
      replyTextList: ""
    };
  }
}

export class ReplyFixedSpeechList extends Model {
  roundNumber = 1;
  replyTextList: string[] = [];
  loading = false;

  get replyText() {
    return this.replyTextList.join("\n");
  }
  set replyText(val: string) {
    this.replyTextList = val.split("\n");
  }

  public objectArrayClasses() {
    return {
      replyTextList: ""
    };
  }
}

export class RuleAsk extends Model {
  wordLimit = [0, 500]; // 题干回答字数
  type = EnumQuestionAppendAskType.EnumQuestionAppendAskTypeAIGC; // 追问话术类型
  aigcType = EnumQuestionAppendAIGCAskType.FOLLOWUP_BOT_COMMON; // AIGC追问配置类型
  conditionType = EnumQuestionAppendConditionType.Count; // 追问条件
  replyFixedSpeechList: ReplyFixedSpeechList[] = [new ReplyFixedSpeechList()]; // 固定追问-追问话术列表
  replyTextPrefix = ""; // // AIGC追问-前置追问话术
  replyCheckpointList: ReplyCheckpointList[] = [new ReplyCheckpointList()]; // // 条件追问-条件列表
  poppycockReplyTextList: string[] = [];

  get poppycockReplyText() {
    return this.poppycockReplyTextList.join("\n");
  }
  set poppycockReplyText(val: string) {
    this.poppycockReplyTextList = val.split("\n");
  }

  public objectArrayClasses() {
    return {
      poppycockReplyTextList: "",
      replyFixedSpeechList: ReplyFixedSpeechList,
      replyCheckpointList: ReplyCheckpointList
    };
  }
}

export class AskConfig extends Model {
  appendScorePercent = 1;
  appendMaxAskNumber = 1;
  enableAppendAsk = 0;
  voiceId = 0;
  ruleList: RuleAsk[] = [];

  static getDemo(questionDesc: string): Promise<string[]> {
    return request
      .postInterviewApi("hr/ai/recommendAppendAsk", {
        questionDesc: questionDesc
      })
      .then(resp => {
        return resp.data.data.clarificationPrompts;
      });
  }

  public objectArrayClasses() {
    return {
      ruleList: RuleAsk
    };
  }
}

export class OptionScore extends Model {
  optionKey = "";
  score = 0;
}

export class DimensionScore extends Model {
  dimensionKey = "";
  videoQuestionScore = 0;
  choiceQuestionScoreList: OptionScore[] = [];
  blankQuestionScoreList = [0];
  dimensionScore = 0;

  public objectArrayClasses() {
    return {
      choiceQuestionScoreList: OptionScore
    };
  }
}

export class QuestionItem extends Model {
  questionId = 0;
  questionType: EnumQuestionType =
    EnumQuestionType.EnumQuestionTypeSingleChoice; // 题型
  preparationTime = 0;
  answerTime = 0;
  minAnswerTime = 0;
  submitNumberLimit = 0;
  openPreparationTimeSound = 0;
  appendAskConfig = new AskConfig();
  dimensionScoreList: DimensionScore[] = [];
  lang = 0; // 语言 src/utils/multi-lang.ts

  // 前端使用的临时参数
  randomTempConfig = new RandomConfig();

  // 前端使用的 是否 disabled 不可用，比如当 配置了 answerTime 的就不能开启追问
  get disabled() {
    return this.answerTime > 0;
  }

  public objectArrayClasses() {
    return {
      disabled: false,
      dimensionScoreList: DimensionScore
    };
  }
}

export class QuestionConfig extends Model {
  questionRandomSort = 0;
  questionRandomSortRange: number[] = [1, 1];
  needSaveVideoBeforeAutoSubmit = 0;
  needParseResume = 0;
  closeQuestionVideo = 0;
  closeObjectiveQuestionVideo = false;
  sectionPageConfig = new SectionConfig().assign(
    ExamComponent.getDefaultSectionPage(SectionPageType.Questions)
  );
  useDeepChat = 0; // 是否使用深度追问(是否开启主观题深度沟通) 0 不使用 1 使用
  maxValidAskTimes = 2; // (深度追问)最大有效沟通轮次 支持 3 4 5 ，使用时 < 3 会当做 3
}

export class RandomConfig extends Model {
  name = "";
  questionIdList: number[] = [];
  questionNumber = 0;
  questionType: EnumQuestionType =
    EnumQuestionType.EnumQuestionTypeSingleChoice; // 题型
  dimensionScoreList: DimensionScore[] = [];

  public objectArrayClasses() {
    return {
      questionIdList: 0,
      dimensionScoreList: DimensionScore
    };
  }
}

export class Question extends Model {
  use = 1;
  componentSort = 0;
  configData = new QuestionConfig();
  questionConfigList: QuestionItem[] = [];
  isRandomQuestion = 0;
  randomConfigList: RandomConfig[] = [];

  public objectArrayClasses() {
    return {
      questionConfigList: QuestionItem,
      randomConfigList: RandomConfig
    };
  }
}
